import { HStack, Text } from "@chakra-ui/react";
import { FeeBasisStats } from "features/fees/dashboard-pages/FeeBasisTab/FeeBasisStats";
import { FeeBasisTable } from "features/fees/dashboard-pages/FeeBasisTab/FeeBasisTable";
import { useRefresh } from "features/fees/dashboard-pages/FeeBasisTab/useRefresh";
import { FeePostUseBreakdownBasisChangeTable } from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisChangeTable";
import {
  useFeeChargeEventPointInTimeTransactionCodesListSuspense,
  useFeeChargeEventsSelectListSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { useViewEditDropdown } from "shared/components/button/ViewEditDropdown";
import { SelectButton } from "shared/components/input/select/SelectButton";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const FeesBasisTab: React.FC = () => {
  const { fundFamilyId } = useRequiredStringParams();
  const { t } = useTranslation(["common", "fees"]);
  const { data: feePeriodSelectData } =
    useFeeChargeEventsSelectListSuspense(fundFamilyId);
  const [feeChargeEventSelected, setFeeChargeEventSelected] = useState(
    feePeriodSelectData.length
      ? feePeriodSelectData[feePeriodSelectData.length - 1].value
      : "",
  );

  const { data: transactionCodeList } =
    useFeeChargeEventPointInTimeTransactionCodesListSuspense(fundFamilyId);

  const selectOptions = useMemo(
    () =>
      feePeriodSelectData.map((element) => (
        <SelectOption value={element.value} key={element.value}>
          {element.rich_value}
        </SelectOption>
      )),
    [feePeriodSelectData],
  );

  const { editModeEnabled, setEditMode, setEditModeDisabled } =
    useViewEditDropdown();

  const { isRefreshLoading } = useRefresh({
    fundFamilyId,
    showToasts: false,
    feeChargeEventSelected,
  });

  return (
    <PageTabBody>
      {/* Hide the FeeBasisTable if there are no codes configured to adjust basis */}
      {!!transactionCodeList.length && (
        <FeeBasisTable
          editModeEnabled={editModeEnabled}
          setEditMode={setEditMode}
          setEditModeDisabled={setEditModeDisabled}
          feeChargeEventSelected={feeChargeEventSelected}
        />
      )}
      <QueryBoundary>
        <HStack flex={1} justifyContent="flex-end" color="gray.500" mb="12px">
          <Text>{t("fees:feeBasisTab.view_quarter")}</Text>
          <SelectButton
            size="sm"
            isFilterButton={true}
            title={t("common:quarter")}
            value={feeChargeEventSelected}
            isOptional={false}
            onChangeValue={(value: string) => {
              setFeeChargeEventSelected(value);
            }}
          >
            {selectOptions}
          </SelectButton>
        </HStack>
        <FeeBasisStats feeChargeEventSelected={feeChargeEventSelected} />
        <FeePostUseBreakdownBasisChangeTable
          fundFamilyId={fundFamilyId}
          feeChargeEventId={feeChargeEventSelected}
          useDraftTransactions={editModeEnabled}
          isLoading={isRefreshLoading}
        />
      </QueryBoundary>
    </PageTabBody>
  );
};
